import { api } from 'restaurantAdmin/api';
import type { ExpandedShift } from '@shared/types/shifts';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import type { ShiftFormData } from './ShiftForm';

interface NewShift {
  startDate: string;
  endDate: string | null;
  repeat: string[];
  name: string;
  startTime: string;
  endTime: string;
  floorPlanTableIds?: string[];
}

export interface UpdatedShift extends NewShift {
  id: string;
}

export interface ShiftUpdateResponse {
  warnings: string[];
  createdShiftIds: string[];
}

export const getShifts = ({
  restaurantId,
  includeEnded,
}: {
  restaurantId: string;
  includeEnded: boolean;
}): Promise<ExpandedShift[]> => {
  const params = new URLSearchParams({ includeEnded: String(includeEnded) });

  return api.get<ExpandedShift[]>(
    `/restaurants/${restaurantId}/shifts?${params}`,
  );
};

export const getShiftsForDate = (
  restaurantId: string,
  date: Date,
  signal?: AbortSignal,
) => {
  const isoDateString = toISODateFormat(date);

  return api.get<ExpandedShift[]>(
    `/restaurants/${restaurantId}/shifts?${new URLSearchParams({
      date: isoDateString,
    })}`,
    {
      signal,
    },
  );
};

export const updateShifts = async (
  restaurantId: string,
  payload: {
    ignoreWarnings: boolean;
    createdShifts: NewShift[];
    updatedShifts: UpdatedShift[];
    deletedShiftIds: string[];
  },
): Promise<ShiftUpdateResponse> =>
  api.post(`/restaurants/${restaurantId}/shifts`, payload);

export const getShiftOccurrences = ({
  restaurantId,
  startDate,
  endDate,
  includeEnded,
}: {
  restaurantId: string;
  startDate: string;
  endDate: string;
  includeEnded: boolean;
}): Promise<ExpandedShift[]> => {
  const params = new URLSearchParams({
    startDate,
    endDate,
    includeEnded: String(includeEnded),
  });

  return api.get<ExpandedShift[]>(
    `/restaurants/${restaurantId}/shift-occurrences?${params}`,
  );
};

export const addOrRemoveTables = async (
  restaurantId: string,
  shiftId: string,
  highlightedFloorPlanTableIds: string[],
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/shifts/${shiftId}/tables`, {
    highlightedFloorPlanTableIds,
  });

export const createShiftOccurrenceOverride = async (
  restaurantId: string,
  shiftId: string,
  occurrenceDate: string,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/shift_occurrence_overrides`, {
    shiftId,
    occurrenceDate,
  });

export type EditShiftOccurrenceRequest = Omit<ShiftFormData, 'isRecurring'> & {
  occurrenceDate: string;
  restaurantId: string;
  shiftId: string;
  applyToAllSubsequentOccurrences: boolean;
};

export const editShiftOccurrence = async (
  request: EditShiftOccurrenceRequest,
): Promise<void> => {
  const { restaurantId, shiftId, ...payload } = request;
  return api.post(
    `/restaurants/${restaurantId}/shifts/${shiftId}/occurrences`,
    {
      name: payload.name,
      startDate: payload.startDate,
      occurrenceDate: payload.occurrenceDate,
      startTime: payload.startTime,
      endDate: payload.endDate,
      endTime: payload.endTime,
      repeat: payload.repeat,
      floorPlanTableIds: payload.floorPlanTableIds,
      applyToAllSubsequentOccurrences: payload.applyToAllSubsequentOccurrences,
    },
  );
};
